import * as i0 from '@angular/core';
import { EventEmitter, afterNextRender, AfterRenderPhase, Directive, Output, inject, Injector, runInInjectionContext, PLATFORM_ID, DestroyRef, ElementRef, NgZone, input, numberAttribute, output, effect, NgModule } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Observable, identity, fromEvent } from 'rxjs';
import { debounceTime, filter } from 'rxjs/operators';
import { isPlatformServer } from '@angular/common';

/**
 * A simple helper directive that emits an event when the element is mounted.
 * This can be useful when a component contains an ng-template that an external component
 * will mount. As a result ngAfterViewInit will not be called on the component when the
 * ng-template is mounted.
 */
class UxaDidMountDirective {
  constructor() {
    /**
     * Emit an event when the element is mounted.
     */
    this.didMount = new EventEmitter();
    // after the next render, emit the didMount event - this allows any positioning to occur before the event is emitted
    // this is using the deprecated signature, it can be updated once we can drop v18
    afterNextRender(() => this.didMount.emit(), {
      phase: AfterRenderPhase.Write
    });
  }
  static {
    this.ɵfac = function UxaDidMountDirective_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || UxaDidMountDirective)();
    };
  }
  static {
    this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
      type: UxaDidMountDirective,
      selectors: [["", "uxaDidMount", ""]],
      outputs: {
        didMount: "uxaDidMount"
      }
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(UxaDidMountDirective, [{
    type: Directive,
    args: [{
      standalone: true,
      selector: '[uxaDidMount]'
    }]
  }], () => [], {
    didMount: [{
      type: Output,
      args: ['uxaDidMount']
    }]
  });
})();

/**
 * Create an observable stream from a resize observer
 * @param target The element to observe
 * @param config The resize observer configuration
 */
function fromResizeObserver(target, config) {
  const injector = config?.injector ?? inject(Injector, {
    optional: true
  });
  if (!injector) {
    throw new Error('The `fromResizeObserver` function requires an injector to be provided in the config when not running in an Angular context.');
  }
  return runInInjectionContext(injector, () => {
    const platform = inject(PLATFORM_ID);
    return new Observable(observer => {
      if (isPlatformServer(platform)) {
        // ResizeObserver is not supported on the server
        return;
      }
      const resizeObserver = new ResizeObserver(mutations => observer.next(mutations));
      resizeObserver.observe(target, config);
      return () => resizeObserver.disconnect();
    });
  });
}
class UxaResizeDirective {
  constructor() {
    this._destroyRef = inject(DestroyRef);
    this._elementRef = inject(ElementRef);
    this._ngZone = inject(NgZone);
    /** Allow the resize event to be debounced */
    this.debounce = input(0, {
      alias: 'uxaResizeDebounce',
      transform: numberAttribute
    });
    /** Emit the resize event */
    this.uxaResize = output();
    fromResizeObserver(this._elementRef.nativeElement).pipe(optionalDebounce(this.debounce()), takeUntilDestroyed(this._destroyRef)).subscribe(event => this._ngZone.run(() => this.uxaResize.emit({
      width: event[0].contentRect.width,
      height: event[0].contentRect.height
    })));
  }
  static {
    this.ɵfac = function UxaResizeDirective_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || UxaResizeDirective)();
    };
  }
  static {
    this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
      type: UxaResizeDirective,
      selectors: [["", "uxaResize", ""]],
      inputs: {
        debounce: [1, "uxaResizeDebounce", "debounce"]
      },
      outputs: {
        uxaResize: "uxaResize"
      }
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(UxaResizeDirective, [{
    type: Directive,
    args: [{
      standalone: true,
      selector: '[uxaResize]'
    }]
  }], () => [], null);
})();
/**
 * Similar to debounceTime, but only debounce if the delay is greater than zero.
 * This is helpful to avoid flickering in cases where we never want to delay the event.
 */
function optionalDebounce(delay) {
  return delay > 0 ? debounceTime(delay) : identity;
}

/**
 * A wrapper around `numberAttribute` that also allows `null` and `undefined` values.
 * By default `numberAttribute` will return `0` for `null` and `undefined` values.
 */
function optionalNumberAttribute(value) {
  return value === null || value === undefined ? undefined : numberAttribute(value);
}

/**
 * Listen to changes on a signal and call a callback when the signal changes
 * @param signal The signal to listen to
 * @param callback The callback to call when the signal changes
 * @param options Configuration options
 */
function onChange(signal, callback, options = {}) {
  effect(() => callback(signal()), {
    injector: options.injector,
    allowSignalWrites: true
  });
}

/**
 * Create an observable stream from a mutation observer
 * @param target The element to observe
 * @param config The mutation observer configuration
 */
function fromMutationObserver(target, config = {}) {
  const platform = config.injector?.get(PLATFORM_ID) ?? inject(PLATFORM_ID, {
    optional: true
  });
  if (!platform) {
    throw new Error('The `fromMutationObserver` function requires an injector to be provided in the config when not running in an Angular context.');
  }
  return new Observable(observer => {
    if (isPlatformServer(platform)) {
      // MutationObserver is not supported on the server
      return;
    }
    const mutationObserver = new MutationObserver(mutations => observer.next(mutations));
    mutationObserver.observe(target, config);
    return () => mutationObserver.disconnect();
  });
}

/**
 * Emits when a click event occurs outside of the target element.
 * @param target The target element to check against.
 */
function fromOutsideClick(target) {
  return fromEvent(document, 'click', {
    capture: true
  }).pipe(filter(event => event.target !== target && !target.contains(event.target)));
}
class UxaUtilsModule {
  static {
    this.ɵfac = function UxaUtilsModule_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || UxaUtilsModule)();
    };
  }
  static {
    this.ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
      type: UxaUtilsModule
    });
  }
  static {
    this.ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({});
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(UxaUtilsModule, [{
    type: NgModule,
    args: [{
      imports: [UxaResizeDirective],
      exports: [UxaResizeDirective]
    }]
  }], null, null);
})();

/**
 * Generated bundle index. Do not edit.
 */

export { UxaDidMountDirective, UxaResizeDirective, UxaUtilsModule, fromMutationObserver, fromOutsideClick, fromResizeObserver, onChange, optionalNumberAttribute };
